export function evidenceOf(state) {
    if (!state.isRepo)
        return { head: null, refs: '', reflogLength: 0, status: '' };
    const refs = state.refs
        .map((r) => `${r.name} ${r.hash}`)
        .sort()
        .join('\n');
    const status = [
        ...state.status.staged.map((p) => `A ${p}`),
        ...state.status.unstaged.map((p) => `M ${p}`),
        ...state.status.untracked.map((p) => `? ${p}`),
        ...state.status.conflicted.map((p) => `U ${p}`),
    ]
        .sort()
        .join('\n');
    return { head: state.head.hash, refs, reflogLength: state.reflog.length, status };
}
function sameEvidence(a, b) {
    return a.head === b.head && a.refs === b.refs && a.reflogLength === b.reflogLength && a.status === b.status;
}
/**
 * The time floor, in seconds, when the mission does not set one.
 *
 * A constant, and a small one, because the floor has to hold for the level
 * with the least work in it. The clock starts when the workspace is seeded
 * and stops when the grade is in, so the least a clear can take is: the
 * shell drawing its prompt, one burst of typing, one command running under
 * emulation, and the checks running under the same emulation. That is
 * about four seconds, and it is the same four seconds on every level. It
 * used to scale with the number of objectives, which measures how much the
 * grader does, not how much the learner does: `git pull` clears a
 * three-objective level and `git rebase main` a six-objective one, and a
 * learner replaying either from memory on a second device (where nothing
 * says the level is verified) lands at six to eight seconds. The floor must
 * sit under that, so it catches the clear that arrives before a prompt
 * could have been typed at, and nothing else.
 */
export function defaultMinSeconds() {
    return 4;
}
/**
 * The floor on work. One: every level changes something, and the guest
 * reports every finished command. A save from the editor counts as work
 * too, because some levels are solved without touching the prompt.
 */
export function defaultMinCommands() {
    return 1;
}
export function assessPlausibility(input) {
    // A verified level is verified. Replaying it fast is what knowing it looks like.
    if (input.replay)
        return [];
    const { mission } = input;
    const minSeconds = mission.plausibility?.minSeconds ?? defaultMinSeconds();
    const minCommands = mission.plausibility?.minCommands ?? defaultMinCommands();
    const work = input.commands + input.saves;
    const flags = [];
    // Evidence first, because it is the one signal that cannot be honest. When
    // there is evidence it settles the question of work either way: a
    // repository that has not moved is a clear nobody did, and a repository
    // that has moved was moved by somebody, however few commands the guest
    // managed to report. The command count only reaches the prompt's
    // PROMPT_COMMAND in a login shell, so a learner who dropped into a nested
    // `bash` or `sh` reports nothing for the rest of the level; the count
    // must never outvote the repository.
    const evidence = mission.expects_repo ? input.evidence : null;
    if (evidence && sameEvidence(evidence.before, evidence.after)) {
        flags.push({
            code: 'no-evidence',
            detail: 'the repository is exactly as the level started it, so nothing here was done',
        });
    }
    else if (!evidence && work < minCommands) {
        // Without a repository (or without evidence of one) the only trace of
        // work is the count of it. Weaker, because a command can be typed and
        // undone, but a clear with no commands and no saves did not happen here.
        flags.push({
            code: 'no-commands',
            detail: work === 0
                ? 'no command was run and no file was saved'
                : `only ${work} command${work === 1 ? '' : 's'} ran, and this level needs at least ${minCommands}`,
        });
    }
    if (input.elapsedMs < minSeconds * 1000) {
        const seconds = Math.round(input.elapsedMs / 100) / 10;
        flags.push({
            code: 'too-fast',
            detail: `it was cleared ${seconds} seconds after opening, and this level cannot be done in under ${minSeconds}`,
        });
    }
    return flags;
}
//# sourceMappingURL=plausibility.js.map